import { getAccountNumberError, getRoutingNumberError } from '@steerfi/shared';
import {
  IconEye,
  IconEyeOff,
  IconLoader2,
  IconPhoto,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { AddressVerificationResponse, StandardizedAddress } from '@/api/address';
import {
  type BusinessAchInfo,
  deleteCompanyLogo,
  getProfile,
  type PartyInfo,
  type PersonalInfo,
  type RevealAchResponse,
  revealAchNumbers,
  type UpdateBusinessAchRequest,
  type UpdatePartyInfoRequest,
  updateBusinessAch,
  updatePartyInfo,
  updatePassword,
  updatePersonalInfo,
  uploadCompanyLogo,
} from '@/api/settings';
import {
  type AddressFormData,
  AddressFormFields,
  AddressVerificationDialog,
  type AddressVerificationStatus,
} from '@/components/address';
import NotificationSettings from '@/components/notification-settings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCompanyLogo } from '@/contexts/company-logo-context';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isAdminTierUser } from '@/lib/authz';
import keycloakService from '@/services/keycloak.service';

type PaymentUploadType = NonNullable<
  UpdatePartyInfoRequest['allowedPaymentFileUploadTypes']
>[number];

const DEFAULT_PAYMENT_UPLOAD_TYPES: PaymentUploadType[] = [];
const PAYMENT_UPLOAD_CHANNEL_OPTIONS: Array<{ value: PaymentUploadType; label: string }> = [
  { value: 'ui_batch', label: 'UI Upload (web browser)' },
  { value: 'sftp', label: 'SFTP' },
];

function normalizeAllowedUploadTypes(
  uploadTypes: UpdatePartyInfoRequest['allowedPaymentFileUploadTypes']
): PaymentUploadType[] {
  if (!uploadTypes) return [];
  return [...new Set(uploadTypes)] as PaymentUploadType[];
}

function getAllowedUploadTypesOrDefault(
  uploadTypes: UpdatePartyInfoRequest['allowedPaymentFileUploadTypes']
): PaymentUploadType[] {
  const normalized = normalizeAllowedUploadTypes(uploadTypes);
  return normalized.length > 0 ? normalized : DEFAULT_PAYMENT_UPLOAD_TYPES;
}

export const Settings = () => {
  const user = useCurrentUser();

  // Company settings are editable by any admin tier — an external (company) admin for
  // their own client, or an internal admin (e.g. for the internal party).
  const showCompanySettings = isAdminTierUser(user?.roles);

  // State for active tab
  const [activeTab, setActiveTab] = useState('user');

  // State for profile data
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo | null>(null);
  const [businessInfo, setBusinessInfo] = useState<BusinessAchInfo | null>(null);
  const [partyInfo, setPartyInfo] = useState<PartyInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // State for editing business info
  const [isEditingBusiness, setIsEditingBusiness] = useState(false);
  const [businessFormData, setBusinessFormData] = useState<UpdateBusinessAchRequest>({
    routingNumber: '',
    accountNumber: '',
    accountType: undefined,
  });

  // State for revealing ACH numbers
  const [revealedAch, setRevealedAch] = useState<RevealAchResponse | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);

  // State for saving
  const [isSaving, setIsSaving] = useState(false);

  // State for editing personal info
  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [personalFormData, setPersonalFormData] = useState({ phoneNumber: '' });
  const [isSavingPersonal, setIsSavingPersonal] = useState(false);

  // State for password change
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  // State for editing company info
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [companyFormData, setCompanyFormData] = useState<UpdatePartyInfoRequest>({
    name: '',
    address1: '',
    address2: '',
    city: '',
    stateCode: '',
    postalCode: '',
    email: '',
    phone: '',
    autoApprovePaymentFiles: false,
    allowedPaymentFileUploadTypes: DEFAULT_PAYMENT_UPLOAD_TYPES,
  });
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  // Shared logo state so the sidebar swaps its logo live after upload/delete.
  const { setLogoUrl: setSidebarLogoUrl } = useCompanyLogo();

  // State for logo upload
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // State for address verification
  const [addressFormData, setAddressFormData] = useState<AddressFormData>({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [addressErrors] = useState<Record<string, string>>({});
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationResult, setVerificationResult] = useState<AddressVerificationResponse | null>(
    null
  );
  const [addressVerificationStatus, setAddressVerificationStatus] =
    useState<AddressVerificationStatus>('not_checked');

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const profile = await getProfile();
      setPersonalInfo(profile.personalInfo);
      setBusinessInfo(profile.businessInfo);
      setPartyInfo(profile.partyInfo);
    } catch (error) {
      toast.error('Failed to load profile');
      // biome-ignore lint/suspicious/noConsole: using in web-app
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load profile data
  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Initialize logo preview from profile data
  useEffect(() => {
    if (partyInfo?.logoUrl) {
      setLogoPreview(partyInfo.logoUrl);
    }
  }, [partyInfo?.logoUrl]);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setIsUploadingLogo(true);
    try {
      const result = await uploadCompanyLogo(logoFile);
      setLogoPreview(result.logoUrl);
      setLogoFile(null);
      setPartyInfo((prev) => (prev ? { ...prev, logoUrl: result.logoUrl } : prev));
      setSidebarLogoUrl(result.logoUrl);
      toast.success('Logo uploaded successfully');
    } catch {
      toast.error('Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    setIsUploadingLogo(true);
    try {
      await deleteCompanyLogo();
      setLogoPreview(null);
      setLogoFile(null);
      setPartyInfo((prev) => {
        if (!prev) return prev;
        const { logoUrl: _, ...rest } = prev;
        return rest;
      });
      setSidebarLogoUrl(null);
      if (logoInputRef.current) logoInputRef.current.value = '';
      toast.success('Logo deleted successfully');
    } catch {
      toast.error('Failed to delete logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleEditBusiness = () => {
    if (!businessInfo) return;

    // Initialize form with current (masked) data
    setBusinessFormData({
      routingNumber: '',
      accountNumber: '',
      accountType: (businessInfo.accountType as 'CHECKING' | 'SAVINGS') || undefined,
    });
    setIsEditingBusiness(true);
    setRevealedAch(null); // Hide revealed numbers when editing
  };

  const handleCancelEdit = () => {
    setIsEditingBusiness(false);
    setBusinessFormData({
      routingNumber: '',
      accountNumber: '',
      accountType: undefined,
    });
    setRevealedAch(null);
  };

  const handleSaveBusiness = async () => {
    // Validate routing number with detailed error messages
    const routingError = getRoutingNumberError(businessFormData.routingNumber);
    if (routingError) {
      toast.error(routingError);
      return;
    }

    // Validate account number with detailed error messages
    const accountError = getAccountNumberError(businessFormData.accountNumber);
    if (accountError) {
      toast.error(accountError);
      return;
    }

    try {
      setIsSaving(true);
      const response = await updateBusinessAch(businessFormData);

      // Update state immediately for instant UI feedback
      // Note: apiCall already unwraps the response, so response IS the data
      setBusinessInfo(response);
      setIsEditingBusiness(false);
      setRevealedAch(null);

      toast.success('Business ACH information updated successfully');
    } catch (error) {
      toast.error('Failed to update business ACH information');
      // biome-ignore lint/suspicious/noConsole: using in web-app
      console.error('Failed to save business ACH information:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevealAch = async () => {
    setIsRevealing(true);
    try {
      const response = await revealAchNumbers();
      setRevealedAch(response);
      toast.success('Full ACH numbers revealed.');
    } catch (error) {
      toast.error('Failed to reveal ACH numbers.');
      // biome-ignore lint/suspicious/noConsole: using in web-app
      console.error('Failed to reveal ACH numbers:', error);
    } finally {
      setIsRevealing(false);
    }
  };

  const handleHideAch = () => {
    setRevealedAch(null);
    toast.info('ACH numbers hidden');
  };

  // Personal info handlers
  const handleEditPersonal = () => {
    setPersonalFormData({
      phoneNumber: personalInfo?.phoneNumber || '',
    });
    setIsEditingPersonal(true);
  };

  const handleCancelPersonalEdit = () => {
    setIsEditingPersonal(false);
    setPersonalFormData({ phoneNumber: '' });
  };

  const handleSavePersonal = async () => {
    setIsSavingPersonal(true);
    try {
      await updatePersonalInfo(personalFormData);

      // Update state immediately for instant UI feedback
      if (personalInfo) {
        setPersonalInfo({
          ...personalInfo,
          phoneNumber: personalFormData.phoneNumber,
        });
      }

      setIsEditingPersonal(false);
      toast.success('Phone number updated successfully');

      // Refresh token in background to sync with Keycloak (for next page load)
      keycloakService.updateToken(-1).catch(() => {
        // Silently fail - phone number is already updated in UI
      });
    } catch (error) {
      toast.error('Failed to update phone number');
      // biome-ignore lint/suspicious/noConsole: using in web-app
      console.error('Failed to update phone number:', error);
    } finally {
      setIsSavingPersonal(false);
    }
  };

  // Password change handler
  const handleUpdatePassword = async () => {
    // Validate passwords
    if (
      !passwordFormData.currentPassword ||
      !passwordFormData.newPassword ||
      !passwordFormData.confirmPassword
    ) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordFormData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }

    setIsSavingPassword(true);
    try {
      await updatePassword({
        currentPassword: passwordFormData.currentPassword,
        newPassword: passwordFormData.newPassword,
      });

      toast.success('Password updated successfully');

      // Clear form
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      toast.error('Failed to update password. Please check your current password and try again.');
      // biome-ignore lint/suspicious/noConsole: using in web-app
      console.error('Failed to update password:', error);
    } finally {
      setIsSavingPassword(false);
    }
  };

  // Company info handlers
  const handleEditCompany = () => {
    setCompanyFormData({
      name: partyInfo?.name || '',
      address1: partyInfo?.address1 || '',
      address2: partyInfo?.address2 || '',
      city: partyInfo?.city || '',
      stateCode: partyInfo?.stateCode || '',
      postalCode: partyInfo?.postalCode || '',
      email: partyInfo?.email || '',
      phone: partyInfo?.phone || '',
      autoApprovePaymentFiles: partyInfo?.autoApprovePaymentFiles || false,
      allowedPaymentFileUploadTypes: getAllowedUploadTypesOrDefault(
        partyInfo?.allowedPaymentFileUploadTypes
      ),
    });
    // Initialize address form data for verification component
    setAddressFormData({
      address1: partyInfo?.address1 || '',
      address2: partyInfo?.address2 || '',
      city: partyInfo?.city || '',
      state: partyInfo?.stateCode || '',
      zipCode: partyInfo?.postalCode || '',
    });
    setAddressVerificationStatus('not_checked');
    setVerificationResult(null);
    setIsEditingCompany(true);
  };

  const handleCancelCompanyEdit = () => {
    setIsEditingCompany(false);
    setCompanyFormData({
      name: '',
      address1: '',
      address2: '',
      city: '',
      stateCode: '',
      postalCode: '',
      email: '',
      phone: '',
      autoApprovePaymentFiles: false,
      allowedPaymentFileUploadTypes: DEFAULT_PAYMENT_UPLOAD_TYPES,
    });
    setAddressFormData({
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
    });
    setAddressVerificationStatus('not_checked');
    setVerificationResult(null);
  };

  // Handle address field changes - sync both form states
  const handleAddressChange = useCallback((field: keyof AddressFormData, value: string) => {
    setAddressFormData((prev) => ({ ...prev, [field]: value }));
    // Also update companyFormData to keep in sync
    if (field === 'state') {
      setCompanyFormData((prev) => ({ ...prev, stateCode: value }));
    } else if (field === 'zipCode') {
      setCompanyFormData((prev) => ({ ...prev, postalCode: value }));
    } else {
      setCompanyFormData((prev) => ({ ...prev, [field]: value }));
    }
    // Reset verification status when address changes
    setAddressVerificationStatus('not_checked');
  }, []);

  // Handle address verification result
  const handleVerificationResult = useCallback((result: AddressVerificationResponse) => {
    setVerificationResult(result);
    // Only show dialog if there are corrections or errors
    if (result.hasCorrections || (result.error && !result.verified)) {
      setVerificationDialogOpen(true);
    }
  }, []);

  // Handle accepting suggested address from USPS
  const handleAcceptSuggestedAddress = useCallback((suggested: StandardizedAddress) => {
    setAddressFormData({
      address1: suggested.streetAddress,
      address2: suggested.secondaryAddress || '',
      city: suggested.city,
      state: suggested.state,
      zipCode: suggested.zipCode,
    });
    setCompanyFormData((prev) => ({
      ...prev,
      address1: suggested.streetAddress,
      address2: suggested.secondaryAddress || '',
      city: suggested.city,
      stateCode: suggested.state,
      postalCode: suggested.zipCode,
    }));
    setAddressVerificationStatus('verified');
    setVerificationDialogOpen(false);
  }, []);

  // Handle keeping original address
  const handleKeepOriginalAddress = useCallback(() => {
    setAddressVerificationStatus('not_verified');
    setVerificationDialogOpen(false);
  }, []);

  const handleSaveCompany = async () => {
    if (!companyFormData.name) {
      toast.error('Company name is required');
      return;
    }
    const selectedUploadTypes = normalizeAllowedUploadTypes(
      companyFormData.allowedPaymentFileUploadTypes
    );

    setIsSavingCompany(true);
    try {
      // Include address verification status in the update
      const updateData = {
        ...companyFormData,
        allowedPaymentFileUploadTypes: selectedUploadTypes,
        addressVerificationStatus: addressVerificationStatus,
        ...(addressVerificationStatus === 'verified' && {
          addressVerifiedAt: new Date().toISOString(),
        }),
      };
      const response = await updatePartyInfo(updateData);

      // Update state immediately for instant UI feedback
      // Note: apiCall already unwraps the response, so response IS the data
      setPartyInfo(response);
      setIsEditingCompany(false);

      toast.success('Company information updated successfully');
    } catch (error) {
      toast.error('Failed to update company information');
      // biome-ignore lint/suspicious/noConsole: using in web-app
      console.error('Failed to update company information:', error);
    } finally {
      setIsSavingCompany(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 items-center justify-center h-full">
        <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header Section */}
      <div className="bg-background border-b border-border">
        <div className="px-6 py-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {personalInfo?.name || 'Loading...'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{personalInfo?.email || ''}</p>
            <div className="mt-2">
              <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-md border border-blue-200">
                Account Admin
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-muted p-1 mb-6">
              <TabsTrigger
                value="user"
                className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground"
              >
                User Settings
              </TabsTrigger>
              {showCompanySettings && (
                <TabsTrigger
                  value="company"
                  className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground"
                >
                  {partyInfo?.name || 'Company'} Settings
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="user" className="mt-0">
              <div>
                {/* Personal Information Section */}
                <Card className="border-border shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle as="h2" className="text-lg font-semibold">
                      Personal Information
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      Update your personal information and account details
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-sm font-medium text-foreground">
                          First Name
                        </Label>
                        <Input
                          id="firstName"
                          value={personalInfo?.firstName || ''}
                          disabled
                          className="bg-muted/40 border-border cursor-not-allowed"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-sm font-medium text-foreground">
                          Last Name
                        </Label>
                        <Input
                          id="lastName"
                          value={personalInfo?.lastName || ''}
                          disabled
                          className="bg-muted/40 border-border cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-foreground">
                        Email Address
                      </Label>
                      <Input
                        id="email"
                        value={personalInfo?.email || ''}
                        disabled
                        className="bg-muted/40 border-border cursor-not-allowed"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-foreground">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        value={
                          isEditingPersonal
                            ? personalFormData.phoneNumber
                            : personalInfo?.phoneNumber || ''
                        }
                        onChange={(e) =>
                          setPersonalFormData({ ...personalFormData, phoneNumber: e.target.value })
                        }
                        disabled={!isEditingPersonal}
                        className={
                          isEditingPersonal
                            ? undefined
                            : 'bg-muted/40 border-border cursor-not-allowed'
                        }
                        placeholder={isEditingPersonal ? 'Enter phone number' : 'Not provided'}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="role" className="text-sm font-medium text-foreground">
                        Role
                      </Label>
                      <Input
                        id="role"
                        value="Account Admin"
                        disabled
                        className="bg-muted/40 border-border cursor-not-allowed"
                      />
                    </div>

                    <div className="flex gap-3 pt-2">
                      {isEditingPersonal ? (
                        <>
                          <Button
                            onClick={handleSavePersonal}
                            disabled={isSavingPersonal}
                            className="bg-gray-900 hover:bg-gray-800 text-white"
                          >
                            {isSavingPersonal ? (
                              <>
                                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              'Save Changes'
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleCancelPersonalEdit}
                            disabled={isSavingPersonal}
                            className="border-border text-foreground hover:bg-muted"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          onClick={handleEditPersonal}
                          className="border-border text-foreground hover:bg-muted"
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Password Section */}
                <Card className="border-border shadow-sm mt-6">
                  <CardHeader className="pb-4">
                    <CardTitle as="h2" className="text-lg font-semibold">
                      Password
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      Change your password to keep your account secure
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="space-y-2">
                      <Label
                        htmlFor="currentPassword"
                        className="text-sm font-medium text-foreground"
                      >
                        Current Password
                      </Label>
                      <Input
                        id="currentPassword"
                        type="password"
                        value={passwordFormData.currentPassword}
                        onChange={(e) =>
                          setPasswordFormData({
                            ...passwordFormData,
                            currentPassword: e.target.value,
                          })
                        }
                        placeholder="Enter current password"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-sm font-medium text-foreground">
                        New Password
                      </Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={passwordFormData.newPassword}
                        onChange={(e) =>
                          setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })
                        }
                        placeholder="Enter new password"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="confirmPassword"
                        className="text-sm font-medium text-foreground"
                      >
                        Confirm New Password
                      </Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={passwordFormData.confirmPassword}
                        onChange={(e) =>
                          setPasswordFormData({
                            ...passwordFormData,
                            confirmPassword: e.target.value,
                          })
                        }
                        placeholder="Confirm new password"
                      />
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={handleUpdatePassword}
                        disabled={isSavingPassword}
                        className="bg-gray-900 hover:bg-gray-800 text-white"
                      >
                        {isSavingPassword ? (
                          <>
                            <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          'Update Password'
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {showCompanySettings && (
              <TabsContent value="company" className="mt-0">
                <div>
                  {/* Company Information Section */}
                  <Card className="border-border shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle as="h2" className="text-lg font-semibold">
                        Company Information
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground">
                        Your company details and contact information
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      {/* Company Logo */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium text-foreground">Company Logo</Label>
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-24 h-24 border border-border rounded-lg overflow-hidden bg-muted/40 flex items-center justify-center">
                            {logoPreview ? (
                              <img
                                src={logoPreview}
                                alt="Company logo"
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <IconPhoto className="h-8 w-8 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <input
                              ref={logoInputRef}
                              type="file"
                              accept="image/jpeg,image/png,image/gif,image/webp"
                              className="hidden"
                              onChange={handleLogoSelect}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => logoInputRef.current?.click()}
                              disabled={isUploadingLogo}
                            >
                              <IconUpload className="h-4 w-4 mr-1" />
                              {logoPreview ? 'Replace' : 'Upload'}
                            </Button>
                            {logoFile && (
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleLogoUpload}
                                disabled={isUploadingLogo}
                              >
                                {isUploadingLogo ? (
                                  <IconLoader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : null}
                                Save Logo
                              </Button>
                            )}
                            {partyInfo?.logoUrl && !logoFile && (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={handleLogoDelete}
                                disabled={isUploadingLogo}
                              >
                                <IconTrash className="h-4 w-4 mr-1" />
                                Remove
                              </Button>
                            )}
                            <p className="text-xs text-muted-foreground">
                              JPEG, PNG, GIF, or WebP. Max 5MB.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="companyName"
                          className="text-sm font-medium text-foreground"
                        >
                          Company Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="companyName"
                          value={isEditingCompany ? companyFormData.name : partyInfo?.name || ''}
                          onChange={(e) =>
                            setCompanyFormData({ ...companyFormData, name: e.target.value })
                          }
                          disabled={!isEditingCompany}
                          className={
                            isEditingCompany
                              ? undefined
                              : 'bg-muted/40 border-border cursor-not-allowed'
                          }
                        />
                      </div>

                      {isEditingCompany ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium text-foreground">
                              Company Address
                            </Label>
                            {addressVerificationStatus === 'verified' && (
                              <span className="text-xs text-green-700 dark:text-green-400 flex items-center gap-1">
                                ✓ USPS Verified
                              </span>
                            )}
                            {addressVerificationStatus === 'not_verified' && (
                              <span className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
                                ⚠ Not verified
                              </span>
                            )}
                          </div>
                          <AddressFormFields
                            address={addressFormData}
                            errors={addressErrors}
                            onChange={handleAddressChange}
                            onVerificationResult={handleVerificationResult}
                            onVerificationStatusChange={setAddressVerificationStatus}
                          />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-foreground">Address</Label>
                          <Input
                            value={partyInfo?.address || 'Not provided'}
                            disabled
                            className="bg-muted/40 border-border cursor-not-allowed"
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label
                          htmlFor="companyEmail"
                          className="text-sm font-medium text-foreground"
                        >
                          Company Email
                        </Label>
                        <Input
                          id="companyEmail"
                          type="email"
                          value={isEditingCompany ? companyFormData.email : partyInfo?.email || ''}
                          onChange={(e) =>
                            setCompanyFormData({ ...companyFormData, email: e.target.value })
                          }
                          disabled={!isEditingCompany}
                          placeholder={isEditingCompany ? 'company@example.com' : 'Not provided'}
                          className={
                            isEditingCompany
                              ? undefined
                              : 'bg-muted/40 border-border cursor-not-allowed'
                          }
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="companyPhone"
                          className="text-sm font-medium text-foreground"
                        >
                          Company Phone
                        </Label>
                        <Input
                          id="companyPhone"
                          type="tel"
                          value={isEditingCompany ? companyFormData.phone : partyInfo?.phone || ''}
                          onChange={(e) =>
                            setCompanyFormData({ ...companyFormData, phone: e.target.value })
                          }
                          disabled={!isEditingCompany}
                          placeholder={isEditingCompany ? '(555) 555-5555' : 'Not provided'}
                          className={
                            isEditingCompany
                              ? undefined
                              : 'bg-muted/40 border-border cursor-not-allowed'
                          }
                        />
                      </div>

                      <div className="space-y-2 pt-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="autoApprove"
                            checked={
                              isEditingCompany
                                ? (companyFormData.autoApprovePaymentFiles ?? false)
                                : (partyInfo?.autoApprovePaymentFiles ?? false)
                            }
                            onCheckedChange={(checked) =>
                              isEditingCompany &&
                              setCompanyFormData({
                                ...companyFormData,
                                autoApprovePaymentFiles: checked === true,
                              })
                            }
                            disabled={!isEditingCompany}
                            className={
                              !isEditingCompany ? 'data-[state=checked]:bg-gray-400' : undefined
                            }
                          />
                          <Label
                            htmlFor="autoApprove"
                            className="text-sm font-medium text-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            Auto-approve Payment Files
                          </Label>
                        </div>
                        <p className="text-sm text-muted-foreground ml-6">
                          When enabled, valid payment files will be automatically approved and move
                          to "Awaiting Funds" status.
                        </p>
                      </div>

                      <div className="space-y-2 pt-2">
                        <Label className="text-sm font-medium text-foreground">
                          Payment File Upload Channels
                        </Label>
                        <div className="flex flex-col gap-2 ml-1">
                          {PAYMENT_UPLOAD_CHANNEL_OPTIONS.map(({ value, label }) => {
                            const currentTypes = isEditingCompany
                              ? getAllowedUploadTypesOrDefault(
                                  companyFormData.allowedPaymentFileUploadTypes
                                )
                              : getAllowedUploadTypesOrDefault(
                                  partyInfo?.allowedPaymentFileUploadTypes
                                );
                            const isChecked = currentTypes.includes(value);
                            return (
                              <div key={value} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`uploadType-${value}`}
                                  checked={isChecked}
                                  onCheckedChange={(checked) => {
                                    if (!isEditingCompany) return;
                                    const prev = getAllowedUploadTypesOrDefault(
                                      companyFormData.allowedPaymentFileUploadTypes
                                    );
                                    const next =
                                      checked === true
                                        ? ([...new Set([...prev, value])] as PaymentUploadType[])
                                        : (prev.filter((t) => t !== value) as PaymentUploadType[]);
                                    setCompanyFormData({
                                      ...companyFormData,
                                      allowedPaymentFileUploadTypes: next,
                                    });
                                  }}
                                  disabled={!isEditingCompany}
                                  className={
                                    !isEditingCompany
                                      ? 'data-[state=checked]:bg-gray-400'
                                      : undefined
                                  }
                                />
                                <Label
                                  htmlFor={`uploadType-${value}`}
                                  className="text-sm text-foreground leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {label}
                                </Label>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-sm text-muted-foreground ml-1">
                          Controls which upload channels are available for payment files.
                        </p>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                        {isEditingCompany ? (
                          <>
                            <Button
                              onClick={handleSaveCompany}
                              disabled={isSavingCompany}
                              className="bg-gray-900 hover:bg-gray-800 text-white"
                            >
                              {isSavingCompany ? (
                                <>
                                  <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                'Save Changes'
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleCancelCompanyEdit}
                              disabled={isSavingCompany}
                              className="border-border text-foreground hover:bg-muted"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline"
                            onClick={handleEditCompany}
                            className="border-border text-foreground hover:bg-muted"
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Notification Settings Section */}
                  <div className="mt-6">
                    <NotificationSettings />
                  </div>

                  {/* Business ACH Information Section */}
                  <Card className="border-border shadow-sm mt-6">
                    <CardHeader className="pb-4">
                      <CardTitle as="h2" className="text-lg font-semibold">
                        Business ACH Information
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground">
                        Your business bank account details for ACH transactions. Sensitive
                        information is masked for security.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      {!businessInfo?.hasBankAccount && !isEditingBusiness ? (
                        <div className="py-6 text-center">
                          <p className="text-sm text-muted-foreground mb-4">
                            No bank account information on file
                          </p>
                          <Button
                            onClick={handleEditBusiness}
                            className="bg-gray-900 hover:bg-gray-800 text-white"
                          >
                            Add Bank Account
                          </Button>
                        </div>
                      ) : isEditingBusiness ? (
                        // Edit Mode
                        <>
                          <div className="space-y-2">
                            <Label
                              htmlFor="routingNumber"
                              className="text-sm font-medium text-foreground"
                            >
                              Routing Number <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="routingNumber"
                              placeholder="9-digit routing number"
                              value={businessFormData.routingNumber}
                              onChange={(e) =>
                                setBusinessFormData({
                                  ...businessFormData,
                                  routingNumber: e.target.value,
                                })
                              }
                              maxLength={9}
                            />
                            <p className="text-xs text-muted-foreground">
                              9-digit bank routing number
                            </p>
                          </div>

                          <div className="space-y-2">
                            <Label
                              htmlFor="accountNumber"
                              className="text-sm font-medium text-foreground"
                            >
                              Account Number <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="accountNumber"
                              type="password"
                              placeholder="Bank account number"
                              value={businessFormData.accountNumber}
                              onChange={(e) =>
                                setBusinessFormData({
                                  ...businessFormData,
                                  accountNumber: e.target.value,
                                })
                              }
                              maxLength={17}
                            />
                            <p className="text-xs text-muted-foreground">
                              4-17 digit account number
                            </p>
                          </div>

                          <div className="space-y-2">
                            <Label
                              htmlFor="accountType"
                              className="text-sm font-medium text-foreground"
                            >
                              Account Type
                            </Label>
                            <Select
                              value={businessFormData.accountType ?? ''}
                              onValueChange={(value: 'CHECKING' | 'SAVINGS') =>
                                setBusinessFormData({ ...businessFormData, accountType: value })
                              }
                            >
                              <SelectTrigger id="accountType">
                                <SelectValue placeholder="Select account type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CHECKING">Checking</SelectItem>
                                <SelectItem value="SAVINGS">Savings</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex gap-3 pt-2">
                            <Button
                              onClick={handleSaveBusiness}
                              disabled={isSaving}
                              className="bg-gray-900 hover:bg-gray-800 text-white"
                            >
                              {isSaving ? (
                                <>
                                  <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                'Save Changes'
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                              className="border-border text-foreground hover:bg-muted"
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      ) : (
                        // View Mode
                        <>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-foreground">
                              Routing Number
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                value={
                                  revealedAch?.routingNumber || businessInfo?.routingNumber || ''
                                }
                                disabled
                                className="bg-muted/40 border-border cursor-not-allowed font-mono"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-foreground">
                              Account Number
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                value={
                                  revealedAch?.accountNumber || businessInfo?.accountNumber || ''
                                }
                                disabled
                                className="bg-muted/40 border-border cursor-not-allowed font-mono"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-foreground">
                              Account Type
                            </Label>
                            <Input
                              value={
                                revealedAch?.accountType ||
                                businessInfo?.accountType ||
                                'Not specified'
                              }
                              disabled
                              className="bg-muted/40 border-border cursor-not-allowed"
                            />
                          </div>

                          <div className="flex gap-3 pt-2">
                            {revealedAch ? (
                              <Button
                                variant="outline"
                                onClick={handleHideAch}
                                className="border-border text-foreground hover:bg-muted"
                              >
                                <IconEyeOff className="mr-2 h-4 w-4" />
                                Hide Numbers
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                onClick={handleRevealAch}
                                disabled={isRevealing}
                                className="border-border text-foreground hover:bg-muted"
                              >
                                {isRevealing ? (
                                  <>
                                    <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Revealing...
                                  </>
                                ) : (
                                  <>
                                    <IconEye className="mr-2 h-4 w-4" />
                                    Reveal Full Numbers
                                  </>
                                )}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              onClick={handleEditBusiness}
                              className="border-border text-foreground hover:bg-muted"
                            >
                              Edit
                            </Button>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {/* Address Verification Dialog */}
      <AddressVerificationDialog
        open={verificationDialogOpen}
        onOpenChange={setVerificationDialogOpen}
        verificationResult={verificationResult}
        onAcceptSuggested={handleAcceptSuggestedAddress}
        onKeepOriginal={handleKeepOriginalAddress}
      />
    </div>
  );
};

export default Settings;
